import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Dialog } from "@mui/material";
import type { Chart as ChartJS } from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { useMira } from "components/GlobalMiraProvider";
import { createHash } from "utils/crypto";
import titleCaseToCamelCase from "utils/titleCaseToCamelCase";
import * as selectors from "selectors";
import actions from "actions";
import { AssessmentType } from "@constants/assessmentTypes";
import SelectDocumentTypeStep from "./SelectDocumentTypeStep";
import PreflightCheckStep from "./PreflightCheckStep";
import ReviewDocumentsStep from "./ReviewDocumentsStep";
import ReviewAssessmentsStep from "./ReviewAssessmentsStep";
import ReviewClinicalGoalsStep from "./ReviewClinicalGoalsStep";
import { AdditionalAttachment, buildAdditionalInformationImages } from "./AdditionalInformationSection";
import ReviewReassessmentGoalsStep from "./ReviewReassessmentGoalsStep";
import ConfirmGenerationStep from "./ConfirmGenerationStep";
import IncidentReportStep, {
  IncidentReportFormState,
  INITIAL_INCIDENT_REPORT_FORM,
  SEVERITY_LEVEL_OPTIONS,
  collapseIncidentReportPayload,
} from "./IncidentReportStep";
import HoursAddendumStep, {
  HoursAddendumFormState,
  INITIAL_HOURS_ADDENDUM_FORM,
  buildHoursAddendumPayload,
  getUniqueCodesFromMapping,
} from "./HoursAddendumStep";
import DischargeReportStep, {
  DischargeReportFormState,
  ExternalProviderOption,
  INITIAL_DISCHARGE_REPORT_FORM,
  buildDischargeReportPayload,
} from "./DischargeReportStep";
import useGetDocumentChecklist, {
  GoalStatus,
} from "hooks/api/documentGeneration/useGetDocumentChecklist";
import useGetDocumentChecklistStatus from "hooks/api/documentGeneration/useGetDocumentChecklistStatus";
import useGetCustomerPhysicians from "hooks/api/client/useGetCustomerPhysicians";
import useGetCurrentAuthApprovedHours from "hooks/api/insurance/useGetCurrentAuthApprovedHours";
import useGetMappedCptCodes from "hooks/api/insurance/useGetMappedCptCodes";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useGlobalToast } from "components/GlobalToastProvider";
import { DOCUMENT_TYPES, DocumentTypeConfig } from "./documentTypes";
import { isGoalNotIncluded, type GoalsViewMode } from "./goalInclusion";

type GenerateDocumentStep =
  | "select"
  | "preflight"
  | "reviewDocuments"
  | "assessments"
  | "goals"
  | "incidentReport"
  | "hoursAddendum"
  | "dischargeReport"
  | "confirm";

/** Label for the button that advances to each step, matching that step's own naming. */
const STEP_CONTINUE_LABELS: Partial<Record<GenerateDocumentStep, string>> = {
  reviewDocuments: "Review Documents",
  assessments: "Review Assessments",
  goals: "Review Clinical Goals",
  incidentReport: "Continue To Incident Report",
  hoursAddendum: "Continue To Hours Addendum",
  dischargeReport: "Continue To Discharge Report",
  confirm: "Continue To Confirm",
};

const INITIAL_TREATMENT_PLAN_KEY = "initial_treatment_plan";
const REASSESSMENT_TREATMENT_PLAN_KEY = "reassessment_treatment_plan";
const NINETY_DAY_PROGRESS_KEY = "90_day_progress";
const INCIDENT_REPORT_KEY = "incident_report";
const HOURS_ADDENDUM_KEY = "hours_addendum_request";
const DISCHARGE_REPORT_KEY = "discharge_report";

/** Goal entry within a status group. Spreads the full CR goal object; progressStatement is appended by the FE for in-progress / mastered. */
export type GoalSubmissionEntry = GoalStatus & {
  progressStatement?: string;
  /** Base64 PNGs of the charts the clinician had visible — one image for cumulative mode, N for individual. */
  chartImages?: string[];
};

/** Goals grouped by lifecycle status, sent to MIRA when generating the document. */
export interface GoalSubmissionPayload {
  inProgress: GoalSubmissionEntry[];
  new: GoalSubmissionEntry[];
  mastered: GoalSubmissionEntry[];
  discontinued: GoalSubmissionEntry[];
  onHold: GoalSubmissionEntry[];
}

export interface GenerateDocumentDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onClose: () => void;
  /** Client ID — used by the data-availability check hook */
  clientId: string;
  /** User ID — used for the document viewer route */
  userId: string;
  /** Full name of the client, displayed in subtitles and cards */
  clientName: string;
}

/**
 * Multi-step dialog for generating a clinical document for a client.
 * Orchestrates step transitions and owns the Dialog shell.
 * Each step is rendered as a co-located component.
 *
 * @param props
 * @param props.open - Whether the dialog is open
 * @param props.onClose - Callback when the dialog should close
 * @param props.clientId - Client ID for the data-availability check
 * @param props.userId - User ID for the document viewer route
 * @param props.clientName - Full name of the client
 */
const GenerateDocumentDialog: React.FC<GenerateDocumentDialogProps> = ({
  open,
  onClose,
  clientId,
  userId,
  clientName,
}) => {
  const [step, setStep] = useState<GenerateDocumentStep>("select");
  const [selectedType, setSelectedType] = useState<DocumentTypeConfig | null>(null);
  const [progressStatements, setProgressStatements] = useState<Record<string | number, string>>({});
  const [selectedTargetIds, setSelectedTargetIds] = useState<Record<string | number, boolean>>({});
  const [additionalInformation, setAdditionalInformation] = useState("");
  const [additionalFiles, setAdditionalFiles] = useState<AdditionalAttachment[]>([]);
  const chartRegistryRef = useRef<
    Map<
      string | number,
      {
        cumulative: ChartJS<"line"> | ChartJS<"bar"> | null;
        targets: Map<string | number, ChartJS<"line"> | ChartJS<"bar">>;
      }
    >
  >(new Map());
  const [includedAssessments, setIncludedAssessments] = useState<AssessmentType[]>([]);
  const [excludedQabfIds, setExcludedQabfIds] = useState<number[]>([]);
  const [documentsReviewed, setDocumentsReviewed] = useState<string[]>([]);
  const [documentsReviewedNarrative, setDocumentsReviewedNarrative] = useState("");
  const [incidentReportForm, setIncidentReportForm] = useState<IncidentReportFormState>(
    INITIAL_INCIDENT_REPORT_FORM
  );
  const [hoursAddendumSeeded, setHoursAddendumSeeded] = useState<HoursAddendumFormState>(
    INITIAL_HOURS_ADDENDUM_FORM
  );
  const [hoursAddendumForm, setHoursAddendumForm] = useState<HoursAddendumFormState>(
    INITIAL_HOURS_ADDENDUM_FORM
  );
  const harUserEditedRef = useRef(false);
  const [dischargeReportForm, setDischargeReportForm] = useState<DischargeReportFormState>(
    INITIAL_DISCHARGE_REPORT_FORM
  );
  const { generate, miraState, setMiraReadyState } = useMira() as any;
  const history = useHistory();
  const { showToast } = useGlobalToast();

  const handleIncidentReportFieldChange = <K extends keyof IncidentReportFormState>(
    field: K,
    value: IncidentReportFormState[K]
  ) => {
    setIncidentReportForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleHoursAddendumJustificationChange = (value: string) => {
    harUserEditedRef.current = true;
    setHoursAddendumForm((prev) => ({ ...prev, clinicalJustification: value }));
  };

  const handleHoursAddendumHoursChange = (code: string, value: number) => {
    harUserEditedRef.current = true;
    setHoursAddendumForm((prev) => ({
      ...prev,
      hours: { ...prev.hours, [code]: value },
    }));
  };

  const handleDischargeReportFieldChange = <K extends keyof DischargeReportFormState>(
    field: K,
    value: DischargeReportFormState[K]
  ) => {
    setDischargeReportForm((prev) => ({ ...prev, [field]: value }));
  };

  const isGenerating = Boolean(miraState?.processData) && !miraState?.response && !miraState?.error;

  // Captured at generate time — selectedType is reset before MIRA finishes.
  const generatedDocRef = useRef<{ title: string; registerType: string }>({
    title: "",
    registerType: "",
  });

  useEffect(() => {
    const response = miraState?.response;
    if (response && typeof response === "object" && response.fileName) {
      const params = new URLSearchParams({
        toolbarAction: "sign-verify",
        suffix: response.fileName,
        type: generatedDocRef.current.registerType,
        title: generatedDocRef.current.title,
        clientId,
        showBack: "true",
      });
      history.push(`/document-viewer/${userId}/generated?${params.toString()}`);
      handleClose();
      setMiraReadyState();
    }
  }, [miraState?.response, userId, clientId]);

  const runGenerate = async () => {
    if (!selectedType) return;
    const documentType = selectedType.key;
    generatedDocRef.current = {
      title: selectedType.title,
      registerType: selectedType.registerType,
    };
    setMiraReadyState();
    const additionalInformationImages = await buildAdditionalInformationImages(additionalFiles);
    const cacheHashId = createHash("md5")
      .update(
        JSON.stringify({
          clientId,
          documentType,
          jhiUserId: String(currentUser?.id),
        })
      )
      .digest("hex");

    const selectedExternalProviderOption = providerOptions.find(
      (p) => p.key === dischargeReportForm.externalProviderKey
    );
    const selectedExternalProviderRow = selectedExternalProviderOption
      ? (physicians ?? []).find(
          (p: any) =>
            (p.id ?? null) === selectedExternalProviderOption.physicianId &&
            (p.clinic_id ?? null) === selectedExternalProviderOption.clinicId
        ) ?? null
      : null;

    const processData = {
      targetProcess: `generateDocuments_${selectedType.miraDocumentType}_v1`,
      platformContext: {
        clientId,
        documentType,
        currentUserId: currentUser?.id,
        // VINELAND and CFQL not relevant for MIRA assessment processing.
        // CPFQ_CHILD and CPFQ_CAREGIVER collapse to the single "cpfq" identifier MIRA already consumes.
        assessments: Array.from(
          new Set(
            includedAssessments
              .filter((type) => type !== "CFQL2" && type !== "VINELAND")
              .map((type) => (type === "CPFQ_CHILD" || type === "CPFQ_CAREGIVER" ? "CPFQ" : type))
              .map(titleCaseToCamelCase)
          )
        ),
        ...(excludedQabfIds.length > 0 && { excludedQabfIds }),
        ...(documentsReviewed.length > 0 && { documentsReviewed }),
        ...(documentsReviewedNarrative.trim() && { documentsReviewedNarrative }),
        goals: buildGoalSubmissionPayload(),
        additionalInformationText: additionalInformation,
        additionalInformationImages,
        ...(selectedType.key === INCIDENT_REPORT_KEY && {
          incidentReport: collapseIncidentReportPayload(incidentReportForm),
        }),
        ...(selectedType.key === HOURS_ADDENDUM_KEY && {
          hoursAddendum: buildHoursAddendumPayload(
            hoursAddendumForm,
            hoursAddendumSeeded,
            mappedCptCodes
          ),
        }),
        ...(selectedType.key === DISCHARGE_REPORT_KEY && {
          dischargeReport: buildDischargeReportPayload(dischargeReportForm),
        }),
        ...((selectedType.key === INITIAL_TREATMENT_PLAN_KEY ||
          selectedType.key === REASSESSMENT_TREATMENT_PLAN_KEY ||
          selectedType.key === NINETY_DAY_PROGRESS_KEY) && {
          externalProviders: physicians ?? [],
        }),
        ...(selectedType.key === DISCHARGE_REPORT_KEY && {
          externalProviders: selectedExternalProviderRow ? [selectedExternalProviderRow] : [],
        }),
        ...(payorRules && {
          payorRules: {
            requiresReferringProvider: payorRules.requiresReferringProvider,
            requiresCriminalLegalHistory: payorRules.requiresCriminalLegalHistory,
            requiresDrugAlcoholUse: payorRules.requiresDrugAlcoholUse,
            requiresTraumaHistory: payorRules.requiresTraumaHistory,
          },
        }),
        // Always wipe the prior req folder and force a fresh init.json upload so MIRA starts clean.
        deleteRequestFolder: true,
        forceUpload: true,
      },
      cacheHashId,
    };
    generate(processData, { pollingDelay: 9000 });
  };

  const runGenerateRef = useRef(runGenerate);
  runGenerateRef.current = runGenerate;
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  useEffect(() => {
    if (!miraState?.error) return;
    showToastRef.current({
      message: "Document generation failed",
      errorState: true,
      retryHandler: () => runGenerateRef.current(),
      errorButtonText: "Retry",
    });
  }, [miraState?.error]);

  const dispatch = useDispatch();
  const currentUser: any = useSelector(selectors.getUser);
  const customerDetails: any = useSelector(selectors.getCustomerDetails);
  const userInsurance: any = useSelector(selectors.getUserInsurance);
  // /clinicians/:id resolves the clinician *record* id, not the user id.
  const primaryClinicianId = customerDetails?.clinician?.clinician_id;
  const selectedUserClinicianId = useSelector(selectors.getUserClinicianId);
  const userClinicianId =
    typeof selectedUserClinicianId === "string" || typeof selectedUserClinicianId === "number"
      ? selectedUserClinicianId
      : undefined;

  const currentClinician =
    useSelector(selectors.getCurrentClinicianId) || currentUser?.clinician_id || primaryClinicianId;

  // Summary counts for the select step (one call for all document types).
  // clientId stays stable so cached statuses persist through the close animation;
  // `enabled: open` pauses fetching while the dialog is closed.
  const { statuses, activeOverride, statusesLoading, refetchStatuses } =
    useGetDocumentChecklistStatus(clientId, currentClinician, { enabled: open });

  // Waives every readiness gate, but only for the one document it was granted for.
  const isOverridden = Boolean(activeOverride) && activeOverride?.documentType === selectedType?.key;

  // Checklist + assessments + goals for the selected document type (preflight/assessments/goals/confirm steps)
  const {
    checklist,
    assessments,
    goals,
    payorRules,
    checklistLoading,
    checklistFetching,
    refetchChecklist,
  } = useGetDocumentChecklist(open ? clientId : undefined, selectedType?.key, currentClinician);

  // Incident Report "Parties Present" options: client, external support team, primary BCBA, supervisor.
  const incidentReportSelected = selectedType?.key === INCIDENT_REPORT_KEY;
  const { teams } = useGetTeams(
    incidentReportSelected && clientId ? { clientIds: clientId } : null
  );
  const incidentTeamId = teams?.[0]?.id;
  const { teamMembers } = useGetTeamMembers(
    incidentReportSelected && incidentTeamId ? { teamIds: incidentTeamId } : null
  );

  const partyOptions = useMemo<string[]>(() => {
    if (!incidentReportSelected) return [];
    const names: string[] = [];
    if (clientName) names.push(clientName);
    const clinician = customerDetails?.clinician;
    if (clinician?.first_name || clinician?.last_name) {
      names.push(`${clinician.first_name ?? ""} ${clinician.last_name ?? ""}`.trim());
    }
    const secondaryClinician = customerDetails?.secondaryClinician;
    if (secondaryClinician?.first_name || secondaryClinician?.last_name) {
      names.push(
        `${secondaryClinician.first_name ?? ""} ${secondaryClinician.last_name ?? ""}`.trim()
      );
    }
    teamMembers?.forEach((m: { name?: string }) => {
      if (m?.name) names.push(m.name);
    });
    return Array.from(new Set(names.filter(Boolean)));
  }, [incidentReportSelected, clientName, customerDetails, teamMembers]);

  const hoursAddendumSelected = selectedType?.key === HOURS_ADDENDUM_KEY;
  useEffect(() => {
    if (hoursAddendumSelected && open && clientId) {
      dispatch(actions.getUserInsurance(parseInt(clientId, 10)));
    }
  }, [hoursAddendumSelected, open, clientId, dispatch]);
  const primaryInsurancePlanId: number | undefined = (userInsurance ?? []).find(
    (i: any) => i?.is_primary && i?.active
  )?.insurance_plan_id;
  const { approvedByCode, currentAuthApprovedHoursFetching } = useGetCurrentAuthApprovedHours(
    hoursAddendumSelected && open ? clientId : undefined
  );
  const { mappedCptCodes, mappedCptCodesFetching } = useGetMappedCptCodes(
    hoursAddendumSelected && open ? primaryInsurancePlanId : undefined
  );

  const dischargeReportSelected = selectedType?.key === DISCHARGE_REPORT_KEY;
  const needsExternalProviders =
    selectedType?.key === INITIAL_TREATMENT_PLAN_KEY ||
    selectedType?.key === REASSESSMENT_TREATMENT_PLAN_KEY ||
    selectedType?.key === NINETY_DAY_PROGRESS_KEY ||
    dischargeReportSelected;
  const { physicians } = useGetCustomerPhysicians(
    needsExternalProviders && open ? clientId : undefined,
    true
  );
  const providerOptions = useMemo<ExternalProviderOption[]>(
    () =>
      (physicians ?? []).map((p: any) => {
        const physicianId: number | null = p.id ?? null;
        const clinicId: number | null = p.clinic_id ?? null;
        return {
          key: `phys${physicianId ?? "Null"}-clin${clinicId ?? "Null"}`,
          physicianId,
          clinicId,
          name: p.name ?? "",
          physicianType: p.physician_type ?? "",
          frequencyCareCoordination: p.frequency_care_coordination ?? null,
          releaseSignedDate: p.release_signed_date ?? null,
          releaseExpiresDate: p.release_expires_date ?? null,
          hasCollaborationNotes: Array.isArray(p.collaboration_notes)
            ? p.collaboration_notes.length > 0
            : false,
        };
      }),
    [physicians]
  );

  useEffect(() => {
    if (!hoursAddendumSelected) return;
    if (currentAuthApprovedHoursFetching) return;
    if (primaryInsurancePlanId && mappedCptCodesFetching) return;

    const hoursByCode = new Map<string, number>();
    for (const e of approvedByCode) {
      const code = e.insuranceCode.code;
      const hours = Number(e.approvedHours ?? 0) || 0;
      hoursByCode.set(code, (hoursByCode.get(code) ?? 0) + hours);
    }

    const codes = getUniqueCodesFromMapping(mappedCptCodes);
    const seededHours: Record<string, number> = {};
    for (const code of codes) {
      seededHours[code] = hoursByCode.get(code) ?? 0;
    }

    const seeded: HoursAddendumFormState = {
      clinicalJustification: "",
      hours: seededHours,
    };
    setHoursAddendumSeeded(seeded);
    if (!harUserEditedRef.current) setHoursAddendumForm(seeded);
  }, [
    hoursAddendumSelected,
    primaryInsurancePlanId,
    approvedByCode,
    mappedCptCodes,
    currentAuthApprovedHoursFetching,
    mappedCptCodesFetching,
  ]);

  const getStatusForType = (documentType: string) => {
    const status = statuses.find((s) => s.documentType === documentType);
    const missingChecklistItems = status?.missingChecklistItems ?? true;
    const missingAssessments = status?.missingAssessments ?? true;
    return {
      missingChecklistItems,
      missingAssessments,
      allDataAvailable: !missingChecklistItems && !missingAssessments,
    };
  };

  const documentTypesWithStatus = Object.values(DOCUMENT_TYPES).map((config) => {
    const { allDataAvailable } = getStatusForType(config.key);
    return { config, allDataAvailable };
  });

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep("select");
      setSelectedType(null);
      setProgressStatements({});
      setSelectedTargetIds({});
      setAdditionalInformation("");
      setAdditionalFiles([]);
      chartRegistryRef.current.clear();
      setIncludedAssessments([]);
      setExcludedQabfIds([]);
      setDocumentsReviewed([]);
      setDocumentsReviewedNarrative("");
      setIncidentReportForm(INITIAL_INCIDENT_REPORT_FORM);
      setHoursAddendumForm(INITIAL_HOURS_ADDENDUM_FORM);
      setHoursAddendumSeeded(INITIAL_HOURS_ADDENDUM_FORM);
      harUserEditedRef.current = false;
      setDischargeReportForm(INITIAL_DISCHARGE_REPORT_FORM);
    }, 250);
  };

  const handleProgressStatementChange = (goalId: string | number, value: string) => {
    setProgressStatements((prev) => ({ ...prev, [goalId]: value }));
  };

  const handleTargetSelectionChange = (targetId: string | number, selected: boolean) => {
    setSelectedTargetIds((prev) => ({ ...prev, [targetId]: selected }));
  };

  const registerChart = (
    goalId: string | number,
    kind: "cumulative" | "target",
    targetId: string | number | null,
    chart: ChartJS<"line"> | ChartJS<"bar"> | null
  ) => {
    let entry = chartRegistryRef.current.get(goalId);
    if (!entry) {
      entry = { cumulative: null, targets: new Map() };
      chartRegistryRef.current.set(goalId, entry);
    }
    if (kind === "cumulative") {
      entry.cumulative = chart;
    } else if (targetId != null) {
      if (chart) entry.targets.set(targetId, chart);
      else entry.targets.delete(targetId);
    }
  };

  const buildChartImagesForGoal = (goalId: string | number): string[] | undefined => {
    const entry = chartRegistryRef.current.get(goalId);
    if (!entry) return undefined;
    if (entry.cumulative) return [entry.cumulative.toBase64Image()];
    if (entry.targets.size > 0) {
      return Array.from(entry.targets.values()).map((chart) => chart.toBase64Image());
    }
    return undefined;
  };

  const buildGoalSubmissionPayload = (): GoalSubmissionPayload => {
    const payload: GoalSubmissionPayload = {
      inProgress: [],
      new: [],
      mastered: [],
      discontinued: [],
      onHold: [],
    };
    const goalsView: GoalsViewMode =
      selectedType?.goalsView === "withProgress"
        ? "withProgress"
        : selectedType?.goalsView === "hoursAddendum"
          ? "hoursAddendum"
          : "withoutProgress";
    goals.forEach((g) => {
      if (isGoalNotIncluded(g, goalsView)) return;
      const chartImages = buildChartImagesForGoal(g.id);
      const base: GoalSubmissionEntry = { ...g, ...(chartImages ? { chartImages } : {}) };
      // ITP / HAR: every included goal is "new" — ignore whatever CR-spread status came through.
      if (goalsView === "withoutProgress" || goalsView === "hoursAddendum") {
        payload.new.push(base);
        return;
      }
      if (g.status == null) {
        payload.new.push(base);
        return;
      }
      switch (g.status) {
        case "in-progress":
          payload.inProgress.push({ ...base, progressStatement: progressStatements[g.id] ?? "" });
          break;
        case "new":
          payload.new.push(base);
          break;
        case "met":
          payload.mastered.push({ ...base, progressStatement: progressStatements[g.id] ?? "" });
          break;
        case "discontinued":
          payload.discontinued.push(base);
          break;
        case "on-hold":
          payload.onHold.push(base);
          break;
      }
    });
    return payload;
  };

  const stepAfterReadiness = (config: DocumentTypeConfig): GenerateDocumentStep => {
    if (config.key === INCIDENT_REPORT_KEY) return "incidentReport";
    if (config.key === HOURS_ADDENDUM_KEY) return "hoursAddendum";
    if (config.key === DISCHARGE_REPORT_KEY) return "dischargeReport";
    return "confirm";
  };

  const stepAfterPreflight = (
    config: DocumentTypeConfig,
    missingAssessments: boolean
  ): GenerateDocumentStep => {
    if (config.key === INITIAL_TREATMENT_PLAN_KEY) return "reviewDocuments";
    if (missingAssessments || config.requiresAssessmentsReview) return "assessments";
    return stepAfterReadiness(config);
  };

  const handleSelectDocumentType = (typeKey: string) => {
    const config = DOCUMENT_TYPES[typeKey];
    if (!config) return;
    setSelectedType(config);

    const { missingChecklistItems, missingAssessments } = getStatusForType(typeKey);
    setStep(missingChecklistItems ? "preflight" : stepAfterPreflight(config, missingAssessments));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={
        step === "assessments" || step === "goals" || step === "reviewDocuments" ? "md" : "sm"
      }
      fullWidth
      PaperProps={{ sx: { pb: 8, overflow: "hidden" } }}
    >
      {step === "select" && (
        <SelectDocumentTypeStep
          clientName={clientName}
          isLoading={statusesLoading}
          documentTypes={documentTypesWithStatus}
          activeOverride={activeOverride}
          onSelectDocumentType={handleSelectDocumentType}
          onClose={handleClose}
        />
      )}
      {step === "preflight" && selectedType && (
        <PreflightCheckStep
          documentTitle={selectedType.title}
          documentIcon={selectedType.icon}
          clientId={clientId}
          primaryClinicianId={primaryClinicianId}
          currentClinicianId={currentClinician}
          userClinicianId={userClinicianId}
          checklist={checklist}
          isFetching={checklistFetching}
          isOverridden={isOverridden}
          continueLabel={
            STEP_CONTINUE_LABELS[
              stepAfterPreflight(selectedType, getStatusForType(selectedType.key).missingAssessments)
            ]
          }
          onContinue={() =>
            setStep(
              stepAfterPreflight(selectedType, getStatusForType(selectedType.key).missingAssessments)
            )
          }
          onBack={() => {
            setStep("select");
            setSelectedType(null);
          }}
          onClose={handleClose}
          onCheckAgain={async () => {
            const [, statusResult] = await Promise.all([refetchChecklist(), refetchStatuses()]);
            const updatedStatuses = statusResult.data?.data ?? [];
            const status = updatedStatuses.find((s) => s.documentType === selectedType?.key);
            if (!status || status.missingChecklistItems) return;
            setStep(stepAfterPreflight(selectedType, status.missingAssessments));
          }}
        />
      )}
      {step === "reviewDocuments" && selectedType && (
        <ReviewDocumentsStep
          isOverridden={isOverridden}
          documentsReviewed={documentsReviewed}
          narrative={documentsReviewedNarrative}
          onDocumentsChange={setDocumentsReviewed}
          onNarrativeChange={setDocumentsReviewedNarrative}
          onClose={handleClose}
          onContinue={() => setStep("assessments")}
        />
      )}
      {step === "assessments" && selectedType && (
        <ReviewAssessmentsStep
          isOverridden={isOverridden}
          documentTitle={selectedType.title}
          clientId={clientId}
          requiredAssessmentTypes={selectedType.requiredAssessmentTypes}
          assessments={assessments}
          payorRequiredAssessments={payorRules?.requiredAssessments ?? null}
          isLoading={checklistLoading}
          onIncludedAssessmentsChange={setIncludedAssessments}
          onExcludedQabfIdsChange={setExcludedQabfIds}
          onBack={() =>
            setStep(
              selectedType.key === INITIAL_TREATMENT_PLAN_KEY ? "reviewDocuments" : "preflight"
            )
          }
          onClose={handleClose}
          onContinue={() => setStep("goals")}
        />
      )}
      {step === "goals" &&
        selectedType &&
        (selectedType.goalsView === "withoutProgress" ||
          selectedType.goalsView === "hoursAddendum") && (
          <ReviewClinicalGoalsStep
            isOverridden={isOverridden}
            documentTitle={selectedType.title}
            clientId={clientId}
            goals={goals}
            goalsView={selectedType.goalsView}
            requiresAtLeastOneGoal={selectedType.requiresAtLeastOneGoal}
            isLoading={checklistLoading}
            additionalInformation={additionalInformation}
            onAdditionalInformationChange={setAdditionalInformation}
            additionalFiles={additionalFiles}
            onAdditionalFilesChange={setAdditionalFiles}
            onBack={() =>
              setStep(selectedType.key === HOURS_ADDENDUM_KEY ? "hoursAddendum" : "assessments")
            }
            onClose={handleClose}
            onContinue={() => setStep("confirm")}
          />
        )}
      {selectedType &&
        selectedType.goalsView === "withProgress" &&
        (step === "goals" || step === "confirm") && (
          <Box sx={{ display: step === "goals" ? "contents" : "none" }}>
            <ReviewReassessmentGoalsStep
              isOverridden={isOverridden}
              documentTitle={selectedType.title}
              clientName={clientName}
              clientId={clientId}
              goals={goals}
              requiresAtLeastOneGoal={selectedType.requiresAtLeastOneGoal}
              isLoading={checklistLoading}
              additionalInformation={additionalInformation}
              onAdditionalInformationChange={setAdditionalInformation}
              additionalFiles={additionalFiles}
              onAdditionalFilesChange={setAdditionalFiles}
              progressStatements={progressStatements}
              onProgressStatementChange={handleProgressStatementChange}
              selectedTargetIds={selectedTargetIds}
              onTargetSelectionChange={handleTargetSelectionChange}
              registerChart={registerChart}
              onBack={() => setStep("assessments")}
              onClose={handleClose}
              onContinue={() => setStep("confirm")}
            />
          </Box>
        )}
      {step === "incidentReport" && selectedType && (
        <IncidentReportStep
          isOverridden={isOverridden}
          form={incidentReportForm}
          onFieldChange={handleIncidentReportFieldChange}
          partyOptions={partyOptions}
          incidentTypeOptions={[
            "Client Injury",
            "Client Self-Injurious Behavior (SIB; no visible injury)",
            "Client Physical Aggression",
            "Client Property Destruction",
            "Caregiver / Other Participant Injury",
            "Environmental Hazard",
            "Other Reportable Incident",
          ].map((v) => ({ value: v, label: v }))}
          severityLevelOptions={SEVERITY_LEVEL_OPTIONS}
          hasCrisisPlan={
            checklist.find((item) => item.name === "has_crisis_plan")?.value as
              | boolean
              | null
              | undefined
          }
          onClose={handleClose}
          onContinue={() => setStep("confirm")}
        />
      )}
      {step === "hoursAddendum" && selectedType && (
        <HoursAddendumStep
          isOverridden={isOverridden}
          form={hoursAddendumForm}
          seeded={hoursAddendumSeeded}
          onClinicalJustificationChange={handleHoursAddendumJustificationChange}
          onHoursChange={handleHoursAddendumHoursChange}
          clientId={clientId}
          clientName={clientName}
          mappedCptCodes={mappedCptCodes}
          isLoading={mappedCptCodesFetching}
          onClose={handleClose}
          onContinue={() => setStep("goals")}
        />
      )}
      {step === "dischargeReport" && selectedType && (
        <DischargeReportStep
          isOverridden={isOverridden}
          form={dischargeReportForm}
          onFieldChange={handleDischargeReportFieldChange}
          providerOptions={providerOptions}
          additionalInformation={additionalInformation}
          onAdditionalInformationChange={setAdditionalInformation}
          additionalFiles={additionalFiles}
          onAdditionalFilesChange={setAdditionalFiles}
          onClose={handleClose}
          onContinue={() => setStep("confirm")}
        />
      )}
      {step === "confirm" && selectedType && (
        <ConfirmGenerationStep
          isOverridden={isOverridden}
          clientName={clientName}
          documentTitle={selectedType.title}
          documentDescription={selectedType.getDescription(clientName)}
          documentIcon={selectedType.icon}
          // Incident report routes back to its own form so the BCBA can edit before re-confirming.
          onBack={() => {
            setMiraReadyState();
            if (selectedType.key === INCIDENT_REPORT_KEY) {
              setStep("incidentReport");
              return;
            }
            if (selectedType.key === HOURS_ADDENDUM_KEY) {
              setStep("goals");
              return;
            }
            if (selectedType.key === DISCHARGE_REPORT_KEY) {
              setStep("dischargeReport");
              return;
            }
            setStep("goals");
          }}
          onClose={handleClose}
          isGenerating={isGenerating}
          onGenerate={() => runGenerate()}
        />
      )}
    </Dialog>
  );
};

export default GenerateDocumentDialog;
